import React, { useState, useEffect, useRef } from 'react';
import { useToast } from '../../src/contexts/ToastContext';
import { Plus, X, Loader2, Save, Upload, Trash2, CheckSquare, Building2, Edit2, Eye, Mic, Square, Sparkles, CheckCircle2, Circle, Check, AlertTriangle } from 'lucide-react';
import { createPortal } from 'react-dom';
import { CRITERIOS_ALTA, CRITERIOS_URGENCIA_EXTRA, criteriosAtendidos, exigeCriterio, textoCriterios } from '../../src/lib/criteriosPrioridade';
import { supabase } from '../../src/lib/supabase';
import { temDepartamento } from '../../src/lib/departamentos';
import { useAuth } from '../../src/contexts/AuthContext';
import { api } from '../../src/services/api';
import { enviarAnexo } from '../../src/lib/anexosTarefa';
import { logAction, logUsageEvent } from '../../src/lib/logger';
import { Negociacao, Profile, Tarefa, TarefaCategoria, TarefaPrioridade, TarefaStatus, SolicitacaoTipo, SolicitacaoDepartamento } from '../../types';
import { EQUIPES_SOLICITACAO, gruposDoDepartamento, tiposDoGrupo, departamentoDoTipo, isTipoOutro, departamentoTemTipos, equipeDoDepartamento } from './solicitacaoTipos';
import { podeIniciarSubmit } from './submitGuard';
import MentionTextarea from './MentionTextarea';
import { prazoDaSolicitacao } from '../../src/lib/prazoSolicitacao';
import { daEquipeDaSolicitacao, podeEntrarNaCentral, podeReceberPedido } from './permissoesSolicitacao';
import TaskDescriptionMarkdown from './TaskDescriptionMarkdown';

interface TaskModalProps {
    isOpen: boolean;
    onClose: () => void;
    onTaskCreated: (task: Tarefa) => void;
    initialNegociacao?: Negociacao | null;
    initialCategoria?: TarefaCategoria;
}

const TaskModal: React.FC<TaskModalProps> = ({ isOpen, onClose, onTaskCreated, initialNegociacao, initialCategoria }) => {
    const { addToast } = useToast();
    const { user, profile } = useAuth();
    const [users, setUsers] = useState<Profile[]>([]);

    const [descriptionMentions, setDescriptionMentions] = useState<Set<string>>(new Set());
    const [descPreviewMode, setDescPreviewMode] = useState<'edit' | 'preview'>('edit');

    const [selectedDepartamento, setSelectedDepartamento] = useState('');

    const [newTask, setNewTask] = useState({
        titulo: '',
        descricao: '',
        responsavel_id: '',
        prioridade: 'media' as TarefaPrioridade,
        vencimento: '',
        recorrencia: '',
        recorrencia_fim: '',
        negociacao_id: initialNegociacao ? initialNegociacao.id : '',
        checklists: [] as { id: string; text: string; completed: boolean }[],
        categoria: 'tarefa' as TarefaCategoria,
        departamento_solicitacao: '' as SolicitacaoDepartamento | '',
        tipo_solicitacao: '' as SolicitacaoTipo | '',
        tipo_outro_detalhe: '',
    });

    // Solicitação não tem departamento — o gate de "desbloquear campos" só se aplica a Tarefa comum
    const fieldsUnlocked = newTask.categoria === 'solicitacao' || !!selectedDepartamento;

    const [files, setFiles] = useState<File[]>([]);
    const [creating, setCreating] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [newChecklistText, setNewChecklistText] = useState('');
    const [editingChecklistId, setEditingChecklistId] = useState<string | null>(null);
    const [editChecklistValue, setEditChecklistValue] = useState('');
    const [urgenciaCount, setUrgenciaCount] = useState(0);
    // Critérios marcados para Alta/Urgência em solicitação (#6179).
    const [criterios, setCriterios] = useState<string[]>([]);
    // Pedido para Financeiro, Faturamento ou Comercial (#6518): quem pede escolhe o prazo e pode
    // mandar para uma pessoa da equipe. Tecnologia e Marketing seguem com o prazo automático pela
    // prioridade e sem dono na criação.
    const pedidoParaEquipe = newTask.categoria === 'solicitacao' && !!newTask.departamento_solicitacao
        && !departamentoTemTipos(newTask.departamento_solicitacao);
    // Para essas equipes Alta/Urgência sempre impede de trabalhar: não pede critério (#6545).
    const pedeCriterio = !pedidoParaEquipe && exigeCriterio(newTask.prioridade, newTask.categoria === 'solicitacao');
    const faltaCriterio = pedeCriterio && !criteriosAtendidos(newTask.prioridade, criterios);
    const alternarCriterio = (id: string) =>
        setCriterios(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
    const prevIsOpenRef = useRef(false);

    // Gravação de áudio → transcrição por IA (só solicitação, ver handleTranscribeAudio)
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [recordingSeconds, setRecordingSeconds] = useState(0);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const recordingStreamRef = useRef<MediaStream | null>(null);
    const recordingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        const justOpened = isOpen && !prevIsOpenRef.current;
        prevIsOpenRef.current = isOpen;
        if (!isOpen || !justOpened) return;

        api.auth.listProfiles().then(setUsers);
        setCriterios([]);

        supabase
            .from('tarefas')
            .select('id', { count: 'exact', head: true })
            .eq('categoria', 'solicitacao')
            .eq('prioridade', 'urgencia')
            .not('solicitacao_status', 'in', '(concluido,rejeitado)')
            .then(({ count }) => setUrgenciaCount(count || 0));

        setNewTask(prev => ({
            ...prev,
            negociacao_id: initialNegociacao ? initialNegociacao.id : '',
            categoria: initialCategoria || 'tarefa'
        }));

        const role = profile?.role;
        if (role === 'vendedor' || role === 'vendedor_parceiro') {
            setSelectedDepartamento(profile?.departamento || '');
            setNewTask(prev => ({ ...prev, responsavel_id: user?.id || '' }));
        } else if (role === 'parceiro') {
            setSelectedDepartamento(profile?.departamento || '');
        }

        // Reset preview mode on open
        setDescPreviewMode('edit');
    }, [isOpen, initialNegociacao]);

    // Solta o microfone se o modal fechar com uma gravação em andamento
    useEffect(() => {
        if (isOpen) return;
        mediaRecorderRef.current?.stop();
        recordingStreamRef.current?.getTracks().forEach(t => t.stop());
        recordingStreamRef.current = null;
        if (recordingIntervalRef.current) clearInterval(recordingIntervalRef.current);
        setIsRecording(false);
        setIsTranscribing(false);
    }, [isOpen]);

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            recordingStreamRef.current = stream;
            audioChunksRef.current = [];

            const recorder = new MediaRecorder(stream);
            mediaRecorderRef.current = recorder;
            recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
            recorder.onstop = () => {
                const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                stream.getTracks().forEach(t => t.stop());
                recordingStreamRef.current = null;
                handleTranscribeAudio(blob);
            };
            recorder.start();

            setIsRecording(true);
            setRecordingSeconds(0);
            recordingIntervalRef.current = setInterval(() => setRecordingSeconds(s => s + 1), 1000);
        } catch (err) {
            console.error(err);
            addToast('Não foi possível acessar o microfone. Verifique a permissão do navegador.', 'error');
        }
    };

    const stopRecording = () => {
        if (recordingIntervalRef.current) {
            clearInterval(recordingIntervalRef.current);
            recordingIntervalRef.current = null;
        }
        setIsRecording(false);
        mediaRecorderRef.current?.stop(); // dispara onstop → handleTranscribeAudio
    };

    const handleTranscribeAudio = async (blob: Blob) => {
        if (blob.size === 0) {
            addToast('Gravação vazia, tente novamente.', 'error');
            return;
        }
        setIsTranscribing(true);
        try {
            const form = new FormData();
            form.append('audio', blob, `solicitacao_${Date.now()}.webm`);

            const { data, error } = await supabase.functions.invoke('transcrever-solicitacao', { body: form });

            if (error) {
                let msg = error.message || 'Erro ao transcrever áudio.';
                try {
                    const ctx = (error as any).context;
                    if (ctx && typeof ctx.json === 'function') {
                        const body = await ctx.json();
                        if (body?.error) msg = body.error;
                    }
                } catch { /* mantém msg genérica */ }
                throw new Error(msg);
            }
            if (!data?.success) throw new Error(data?.error || 'Erro ao transcrever áudio.');

            const { descricao, titulo, tipo_solicitacao } = data.data as { descricao: string; titulo: string; tipo_solicitacao: SolicitacaoTipo | null };
            setNewTask(prev => ({
                ...prev,
                descricao: prev.descricao.trim() ? `${prev.descricao}\n\n${descricao}` : descricao,
                titulo: prev.titulo.trim() ? prev.titulo : (titulo || prev.titulo),
                tipo_solicitacao: prev.tipo_solicitacao || tipo_solicitacao || prev.tipo_solicitacao,
                // O transcritor hoje só reconhece o vocabulário de TI — deriva o departamento do
                // tipo sugerido pra não deixar o select de Tipo desabilitado depois da transcrição.
                departamento_solicitacao: prev.departamento_solicitacao || departamentoDoTipo(tipo_solicitacao) || prev.departamento_solicitacao,
            }));
            addToast('Áudio transcrito! Revise os campos antes de criar.', 'success');
        } catch (err: any) {
            console.error(err);
            addToast('Erro ao transcrever áudio: ' + (err?.message || 'tente novamente.'), 'error');
        } finally {
            setIsTranscribing(false);
        }
    };

    const hojeIso = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
    // O prazo pode ser no mesmo dia, inclusive fim de semana e feriado (#6545).
    const prazoMinimo = hojeIso;
    const pessoasDaEquipe = pedidoParaEquipe
        ? users.filter(u => u.status !== 'inactive' && podeEntrarNaCentral(u) && podeReceberPedido(u)
            && daEquipeDaSolicitacao({ departamento_solicitacao: newTask.departamento_solicitacao || null }, u))
        : [];

    const handleCreateTask = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!podeIniciarSubmit(creating)) return;
        if (pedidoParaEquipe && (!newTask.vencimento || newTask.vencimento < prazoMinimo)) {
            addToast(`Escolha um prazo a partir de hoje (${new Date(`${prazoMinimo}T12:00:00`).toLocaleDateString('pt-BR')}).`, 'warning');
            return;
        }
        if (faltaCriterio) {
            addToast(newTask.prioridade === 'urgencia'
                ? 'Para Urgência, marque um critério da Alta e um critério extra.'
                : 'Para Alta, marque qual critério se aplica.', 'warning');
            return;
        }
        setCreating(true);
        setUploading(true);

        try {
            const uploadedUrls: string[] = [];
            // Um anexo que falha (arquivo grande, rede instável, nome esquisito) não pode derrubar
            // a criação inteira — o usuário perderia título/descrição já preenchidos e a sensação
            // seria "anexo não funciona" quando na real foi só aquele arquivo. Cada upload roda no
            // seu próprio try/catch; o que falhar entra em failedFiles e vira um aviso à parte,
            // depois de a tarefa/solicitação já ter sido criada com o que deu certo.
            const failedFiles: string[] = [];

            if (files.length > 0) {
                for (const file of files) {
                    try {
                        // Mesmo envio do painel da solicitação (src/lib/anexosTarefa.ts):
                        // caminho com sufixo aleatorio — `Date.now()` sozinho colidia quando
                        // dois arquivos de mesmo nome eram escolhidos juntos — e nova
                        // tentativa automatica em falha transitoria de rede.
                        const { url } = await enviarAnexo(file);
                        uploadedUrls.push(url);
                    } catch (uploadErr: any) {
                        console.error('Erro ao enviar anexo:', file.name, uploadErr);
                        failedFiles.push(file.name);
                    }
                }
            }

            const isSolicitacao = newTask.categoria === 'solicitacao';

            const taskData = {
                titulo: newTask.titulo,
                // O critério de Alta/Urgência vai junto, para quem atende ver o motivo (#6179).
                descricao: pedeCriterio
                    ? [newTask.descricao.trim(), textoCriterios(newTask.prioridade, criterios)].filter(Boolean).join('\n\n')
                    : newTask.descricao,
                // Solicitação nasce sem dono — quem for tratar se atribui depois via "Transferir"
                // Solicitação nasce sem dono, exceto pedido para outra equipe com pessoa escolhida (#6518).
                responsavel_id: isSolicitacao ? (pedidoParaEquipe ? newTask.responsavel_id || null : null) : newTask.responsavel_id,
                prioridade: newTask.prioridade,
                vencimento: newTask.vencimento || null,
                solicitante_id: user?.id,
                status: 'a_iniciar' as TarefaStatus,
                anexos_url: uploadedUrls,
                recorrencia: newTask.recorrencia || null,
                recorrencia_fim: newTask.recorrencia_fim || null,
                negociacao_id: newTask.negociacao_id || null,
                checklists: newTask.checklists || [],
                // Tarefa administrativa nunca é visita: visita só nasce pelo CriarVisitaModal.
                tipo: null,
                categoria: newTask.categoria,
                departamento_solicitacao: isSolicitacao ? newTask.departamento_solicitacao || null : null,
                // Só Tecnologia e Marketing têm tipo; a transcrição por áudio pode ter sugerido um para outra equipe.
                tipo_solicitacao: isSolicitacao && departamentoTemTipos(newTask.departamento_solicitacao || null) ? newTask.tipo_solicitacao || null : null,
                tipo_outro_detalhe: isSolicitacao && departamentoTemTipos(newTask.departamento_solicitacao || null) && isTipoOutro(newTask.tipo_solicitacao || null) ? newTask.tipo_outro_detalhe.trim() || null : null,
                solicitacao_status: isSolicitacao ? 'novo' : null,
                created_at: new Date().toISOString()
            };

            const { data, error } = await supabase.from('tarefas').insert([taskData]).select().single();

            if (error) throw error;

            onTaskCreated(data as Tarefa);

            await logAction(user?.id, 'CREATE_TAREFA', {
                tarefa_id: data.id,
                titulo: data.titulo,
                responsavel: users.find(u => u.id === newTask.responsavel_id)?.full_name
            });
            logUsageEvent({ userId: user?.id, modulo: 'tarefas', funcionalidade: isSolicitacao ? 'solicitacoes' : null, acao: 'criar_tarefa', origem: 'acao' });

            // Pedido para uma equipe (#6518): avisa no sino os líderes dela, menos quem abriu.
            // Com pessoa escolhida, o aviso de atribuição do banco já chega a ela; o líder só é avisado da fila.
            const lideres = isSolicitacao && !(pedidoParaEquipe && newTask.responsavel_id) ? (equipeDoDepartamento(newTask.departamento_solicitacao || null)?.avisar || []).filter(id => id !== user?.id) : [];
            if (lideres.length > 0) {
                await supabase.from('notifications').insert(lideres.map(id => ({
                    user_id: id,
                    type: 'task_assigned',
                    title: 'Nova solicitação para a sua equipe',
                    content: `${profile?.full_name || 'Alguém'} pediu: ${newTask.titulo}`,
                    // Formato de solicitação: o sino abre o modal direto, sem navegar (idDaSolicitacaoNoLink).
                    link: `/tarefas/solicitacoes?id=${data.id}`,
                })));
            }

            // Aviso na tela de quem vai atender (#6544): a pessoa escolhida ou, na fila, os líderes.
            // Best-effort: a solicitação já foi criada; sem o aviso, o sino continua avisando.
            if (pedidoParaEquipe) {
                const quemAtende = (newTask.responsavel_id ? [newTask.responsavel_id] : equipeDoDepartamento(newTask.departamento_solicitacao || null)?.avisar || [])
                    .filter(id => id && id !== user?.id);
                if (quemAtende.length > 0) {
                    const { error: avisoErr } = await supabase.from('avisos_solicitacao')
                        .insert(quemAtende.map(id => ({ user_id: id, tarefa_id: data.id, tipo: 'recebida' })));
                    if (avisoErr) console.error('Erro ao gravar o aviso da solicitação:', avisoErr);
                }
            }

            if (descriptionMentions.size > 0) {
                const notifications = Array.from(descriptionMentions).map(mentionedUserId => ({
                    user_id: mentionedUserId,
                    titulo: `Você foi marcado em uma nova tarefa`,
                    mensagem: `${profile?.full_name} mencionou você na tarefa "${newTask.titulo}"`,
                    link: `/tarefas?id=${data.id}`,
                    lida: false
                }));

                await supabase.from('notificacoes').insert(notifications);
                setDescriptionMentions(new Set());
            }

            onClose();

            setNewTask({
                titulo: '',
                descricao: '',
                responsavel_id: '',
                prioridade: 'media',
                vencimento: '',
                recorrencia: '',
                recorrencia_fim: '',
                negociacao_id: '',
                checklists: [],
                categoria: 'tarefa',
                departamento_solicitacao: '',
                tipo_solicitacao: '',
                tipo_outro_detalhe: '',
            });
            setSelectedDepartamento('');
            setCriterios([]);
            setFiles([]);
            setDescPreviewMode('edit');
            // Diz pra onde foi: a Central agora filtra por equipe, e quem pede para outra equipe não
            // a vê atuando — só o que abriu — então a confirmação precisa nomear o destino.
            addToast(isSolicitacao
                ? `Solicitação criada${newTask.departamento_solicitacao ? ` e enviada para ${newTask.departamento_solicitacao}` : ''}!`
                : 'Tarefa criada com sucesso!', 'success');
            if (failedFiles.length > 0) {
                addToast(
                    `${failedFiles.length === 1 ? `O anexo "${failedFiles[0]}" não` : `Os anexos "${failedFiles.join('", "')}" não`} foi${failedFiles.length === 1 ? '' : 'ram'} enviado${failedFiles.length === 1 ? '' : 's'}. Tente adicionar de novo pela tela de detalhes.`,
                    'error'
                );
            }

        } catch (error: any) {
            console.error(error);
            if (error?.code === 'P0001' || (error?.message || '').includes('urgentes em aberto')) {
                addToast('Limite de 3 solicitações urgentes em aberto atingido.', 'error');
            } else {
                // Sem a mensagem crua do banco (o detalhe já foi pro console acima).
                addToast(newTask.categoria === 'solicitacao'
                    ? 'Não foi possível criar a solicitação. Confira os campos e tente de novo.'
                    : 'Não foi possível criar a tarefa. Confira os campos e tente de novo.', 'error');
            }
        } finally {
            setCreating(false);
            setUploading(false);
        }
    };

    return createPortal(
        <div
            className={`fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in ${!isOpen ? 'hidden' : ''}`}
            onClick={onClose}
        >
            <div
                className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh] md:max-h-[90vh]"
                onClick={e => e.stopPropagation()}
            >
                <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-800/50 flex justify-between items-center bg-gray-50 dark:bg-slate-800/50">
                    <h3 className="font-bold text-lg text-gray-800 dark:text-white flex items-center gap-2">
                        <Plus size={20} className="text-primary" />
                        {newTask.categoria === 'solicitacao' ? 'Nova Solicitação' : 'Nova Tarefa'}
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleCreateTask} className="p-4 md:p-6 overflow-y-auto space-y-4">
                    {!initialCategoria && (
                        <div className="space-y-1">
                            <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Categoria <span className="text-red-500">*</span></label>
                            <div className="inline-flex rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                                <button
                                    type="button"
                                    onClick={() => setNewTask(prev => ({ ...prev, categoria: 'tarefa', responsavel_id: '' }))}
                                    className={`px-4 py-2 text-sm font-medium transition-colors ${newTask.categoria === 'tarefa' ? 'bg-primary text-white' : 'bg-white dark:bg-slate-900 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800'}`}
                                >
                                    Tarefa
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setNewTask(prev => ({ ...prev, categoria: 'solicitacao', responsavel_id: '' }))}
                                    className={`px-4 py-2 text-sm font-medium transition-colors border-l border-gray-200 dark:border-slate-700 ${newTask.categoria === 'solicitacao' ? 'bg-primary text-white' : 'bg-white dark:bg-slate-900 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800'}`}
                                >
                                    Solicitação (pedido a uma equipe)
                                </button>
                            </div>
                            {newTask.categoria === 'solicitacao' && (
                                <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">Peça algo a Tecnologia e Marketing, Financeiro, Faturamento, Comercial ou Suporte técnico.</p>
                            )}
                        </div>
                    )}

                    {newTask.categoria === 'solicitacao' && (
                        <div className="rounded-xl border border-dashed border-primary/30 bg-primary/5 dark:bg-primary/10 p-4 flex items-center gap-4 flex-wrap">
                            <div className="flex-1 min-w-[180px]">
                                <p className="text-sm font-semibold text-gray-700 dark:text-slate-200 flex items-center gap-1.5">
                                    <Sparkles size={14} className="text-primary" /> Prefere falar?
                                </p>
                                <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                                    Grave um áudio descrevendo o problema, o ajuste ou a ideia — a gente transcreve e sugere Título e Tipo pra você revisar antes de criar.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={isRecording ? stopRecording : startRecording}
                                disabled={isTranscribing}
                                className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${isRecording
                                    ? 'bg-rose-600 text-white hover:bg-rose-700'
                                    : 'bg-white dark:bg-slate-900 text-primary border border-primary/30 hover:bg-primary/10'
                                    }`}
                            >
                                {isTranscribing ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" /> Transcrevendo...
                                    </>
                                ) : isRecording ? (
                                    <>
                                        <Square size={14} /> Parar ({Math.floor(recordingSeconds / 60)}:{String(recordingSeconds % 60).padStart(2, '0')})
                                    </>
                                ) : (
                                    <>
                                        <Mic size={16} /> Gravar por áudio
                                    </>
                                )}
                            </button>
                        </div>
                    )}

                    {newTask.categoria === 'solicitacao' && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label htmlFor="solicitacao-equipe" className="text-sm font-medium text-gray-700 dark:text-slate-300">Equipe <span className="text-red-500">*</span></label>
                                <select
                                    id="solicitacao-equipe"
                                    required
                                    value={newTask.departamento_solicitacao}
                                    onChange={e => {
                                        const departamento = e.target.value as SolicitacaoDepartamento | '';
                                        // Troca de departamento invalida o tipo escolhido — cada um tem seu próprio vocabulário
                                        setNewTask(prev => ({ ...prev, departamento_solicitacao: departamento, tipo_solicitacao: '', tipo_outro_detalhe: '', responsavel_id: '', vencimento: '' }));
                                    }}
                                    className="w-full px-4 py-2 border border-gray-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-white"
                                >
                                    <option value="" className="dark:bg-slate-900">Selecione...</option>
                                    {/* Tecnologia e Marketing seguem como dois destinos (cada um com seus tipos)
                                        agrupados numa equipe; as outras equipes são um destino só (#6518). */}
                                    {EQUIPES_SOLICITACAO.map(eq => eq.departamentos.length > 1 ? (
                                        <optgroup key={eq.id} label={eq.label} className="dark:bg-slate-900">
                                            {eq.departamentos.map(dep => <option key={dep} value={dep} className="dark:bg-slate-900">{dep}</option>)}
                                        </optgroup>
                                    ) : (
                                        <option key={eq.id} value={eq.departamentos[0]} className="dark:bg-slate-900">{eq.label}</option>
                                    ))}
                                </select>
                            </div>
                            {(!newTask.departamento_solicitacao || departamentoTemTipos(newTask.departamento_solicitacao)) && (
                            <div className="space-y-1">
                                <label htmlFor="solicitacao-tipo" className="text-sm font-medium text-gray-700 dark:text-slate-300">Tipo de Solicitação <span className="text-red-500">*</span></label>
                                <select
                                    id="solicitacao-tipo"
                                    required
                                    disabled={!newTask.departamento_solicitacao}
                                    value={newTask.tipo_solicitacao}
                                    onChange={e => {
                                        const tipo = e.target.value as SolicitacaoTipo | '';
                                        setNewTask(prev => ({ ...prev, tipo_solicitacao: tipo, tipo_outro_detalhe: isTipoOutro(tipo || null) ? prev.tipo_outro_detalhe : '' }));
                                    }}
                                    className="w-full px-4 py-2 border border-gray-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <option value="" className="dark:bg-slate-900">{newTask.departamento_solicitacao ? 'Selecione...' : 'Escolha a equipe primeiro'}</option>
                                    {newTask.departamento_solicitacao && gruposDoDepartamento(newTask.departamento_solicitacao).map(grupo => (
                                        <optgroup key={grupo} label={grupo} className="dark:bg-slate-900">
                                            {tiposDoGrupo(grupo, newTask.departamento_solicitacao as SolicitacaoDepartamento).map(t => (
                                                <option key={t.value} value={t.value} title={t.descricao} className="dark:bg-slate-900">{t.label}</option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                            </div>
                            )}
                            {departamentoTemTipos(newTask.departamento_solicitacao || null) && isTipoOutro(newTask.tipo_solicitacao || null) && (
                                <div className="space-y-1 sm:col-span-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Qual o tipo da solicitação? <span className="text-red-500">*</span></label>
                                    <input
                                        required
                                        type="text"
                                        maxLength={140}
                                        value={newTask.tipo_outro_detalhe}
                                        onChange={e => setNewTask({ ...newTask, tipo_outro_detalhe: e.target.value })}
                                        placeholder="Descreva em poucas palavras do que se trata"
                                        className="w-full px-4 py-2 border border-gray-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-white"
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {pedidoParaEquipe && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label htmlFor="pedido-prazo" className="text-sm font-medium text-gray-700 dark:text-slate-300">Prazo <span className="text-red-500">*</span></label>
                                <input
                                    id="pedido-prazo"
                                    required
                                    type="date"
                                    min={prazoMinimo}
                                    value={newTask.vencimento}
                                    onChange={e => setNewTask({ ...newTask, vencimento: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-white color-scheme-dark"
                                />
                                <p className="text-xs text-gray-500 dark:text-slate-400">A partir de hoje, inclusive fim de semana e feriado.</p>
                            </div>
                            <div className="space-y-1">
                                <label htmlFor="pedido-pessoa" className="text-sm font-medium text-gray-700 dark:text-slate-300">Para quem <span className="text-gray-400 font-normal">(opcional)</span></label>
                                <select
                                    id="pedido-pessoa"
                                    value={newTask.responsavel_id}
                                    onChange={e => setNewTask({ ...newTask, responsavel_id: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-white"
                                >
                                    <option value="" className="dark:bg-slate-900">Fila da equipe (quem puder assume)</option>
                                    {pessoasDaEquipe.map(u => <option key={u.id} value={u.id} className="dark:bg-slate-900">{u.full_name}</option>)}
                                </select>
                            </div>
                        </div>
                    )}

                    <div className={`grid grid-cols-1 ${newTask.categoria === 'tarefa' ? 'md:grid-cols-2' : ''} gap-4`}>
                        <div className="space-y-1">
                            <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Solicitante</label>
                            <input
                                type="text"
                                disabled
                                value={profile?.full_name || user?.email || ''}
                                className="w-full px-4 py-2 bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-sm text-gray-500 dark:text-slate-400"
                            />
                        </div>

                        {newTask.categoria === 'tarefa' && (
                            <div className="space-y-1">
                                <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Vincular a Negociação</label>
                                <input
                                    type="text"
                                    disabled={!!initialNegociacao}
                                    value={initialNegociacao ? initialNegociacao.titulo : (newTask.negociacao_id ? 'Negociação Selecionada' : '')}
                                    placeholder={initialNegociacao ? '' : 'Nenhuma negociação vinculada'}
                                    className="w-full px-4 py-2 bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-sm text-gray-700 dark:text-slate-200 font-medium"
                                />
                            </div>
                        )}
                    </div>

                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-slate-300">{newTask.categoria === 'solicitacao' ? 'Título da Solicitação' : 'Título da Tarefa'} <span className="text-red-500">*</span></label>
                        <input
                            required
                            type="text"
                            value={newTask.titulo}
                            onChange={e => setNewTask({ ...newTask, titulo: e.target.value })}
                            className="w-full px-4 py-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg text-sm dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                            placeholder="Resumo da atividade"
                        />
                    </div>

                    {/* Descrição com toggle Edit/Preview estilo GitHub */}
                    <div className="space-y-0">
                        <label className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-1 block">Descrição Detalhada</label>

                        {/* GitHub-style tab bar */}
                        <div className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
                            <div className="flex items-center gap-0 bg-gray-50 dark:bg-slate-800/80 border-b border-gray-200 dark:border-slate-700 px-1">
                                <button
                                    type="button"
                                    onClick={() => setDescPreviewMode('edit')}
                                    className={`relative px-3.5 py-2 flex items-center gap-1.5 text-xs font-medium transition-colors rounded-t-md
                                        ${descPreviewMode === 'edit'
                                            ? 'text-gray-800 dark:text-white bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 border-b-white dark:border-b-slate-900 -mb-px'
                                            : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 border border-transparent'}`}
                                >
                                    <Edit2 size={12} /> Editar
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setDescPreviewMode('preview')}
                                    className={`relative px-3.5 py-2 flex items-center gap-1.5 text-xs font-medium transition-colors rounded-t-md
                                        ${descPreviewMode === 'preview'
                                            ? 'text-gray-800 dark:text-white bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 border-b-white dark:border-b-slate-900 -mb-px'
                                            : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 border border-transparent'}`}
                                >
                                    <Eye size={12} /> Preview
                                </button>
                            </div>

                            {/* Content area */}
                            {descPreviewMode === 'edit' ? (
                                <div className="p-0">
                                    <MentionTextarea
                                        users={users}
                                        value={newTask.descricao}
                                        onChange={e => setNewTask({ ...newTask, descricao: e.target.value })}
                                        onMention={(userId) => setDescriptionMentions(prev => new Set(prev).add(userId))}
                                        className="w-full px-4 py-2.5 bg-white dark:bg-slate-900 border-0 text-xs dark:text-white focus:ring-0 outline-none resize-y min-h-[120px] font-mono leading-relaxed rounded-b-lg"
                                        placeholder="Descreva o que precisa ser feito... (suporta **markdown**, @ para mencionar)"
                                        rows={4}
                                    />
                                </div>
                            ) : (
                                <div className="min-h-[120px] px-4 py-3 bg-white dark:bg-slate-900 overflow-y-auto max-h-[240px] rounded-b-lg">
                                    {newTask.descricao.trim() ? (
                                        <TaskDescriptionMarkdown value={newTask.descricao} variant="compact" />
                                    ) : (
                                        <p className="text-gray-400 dark:text-slate-600 text-xs italic text-center mt-8">
                                            Nada para visualizar...
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Dica */}
                        <p className="text-[10px] text-gray-400 dark:text-slate-600 mt-1.5">
                            Suporta **negrito**, *itálico*, `código`, # títulos, listas e tabelas markdown.
                        </p>
                    </div>

                    {/* Departamento — não se aplica a Solicitação */}
                    {newTask.categoria === 'tarefa' && !['vendedor', 'vendedor_parceiro', 'parceiro'].includes(profile?.role || '') ? (
                        <div className="space-y-1">
                            <label className="text-sm font-medium text-gray-700 dark:text-slate-300 flex items-center gap-1.5">
                                <Building2 size={14} className="text-gray-400" />
                                Departamento <span className="text-red-500">*</span>
                            </label>
                            <select
                                required
                                value={selectedDepartamento}
                                onChange={e => {
                                    setSelectedDepartamento(e.target.value);
                                    setNewTask(prev => ({ ...prev, responsavel_id: '' }));
                                }}
                                className="w-full px-4 py-2 border border-gray-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-white"
                            >
                                <option value="" className="dark:bg-slate-900">Selecione o departamento...</option>
                                <option value="Comercial" className="dark:bg-slate-900">Comercial</option>
                                <option value="Parcerias" className="dark:bg-slate-900">Parcerias</option>
                                <option value="Financeiro" className="dark:bg-slate-900">Financeiro</option>
                                <option value="Marketing" className="dark:bg-slate-900">Marketing</option>
                                <option value="Tecnologia" className="dark:bg-slate-900">Tecnologia</option>
                                <option value="Administrativo" className="dark:bg-slate-900">Administrativo</option>
                                <option value="Suporte e Atendimento" className="dark:bg-slate-900">Suporte e Atendimento</option>
                            </select>
                            {!selectedDepartamento && (
                                <p className="text-xs text-amber-500 dark:text-amber-400 mt-1">Selecione um departamento para desbloquear os campos abaixo.</p>
                            )}
                        </div>
                    ) : null}

                    <div className={`grid grid-cols-1 ${newTask.categoria === 'tarefa' ? 'md:grid-cols-3' : 'md:grid-cols-1'} gap-4 transition-opacity ${!fieldsUnlocked ? 'opacity-50 pointer-events-none' : ''}`}>
                        {newTask.categoria === 'tarefa' && (
                            <div className="space-y-1">
                                <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Responsável <span className="text-red-500">*</span></label>
                                <select
                                    required
                                    value={newTask.responsavel_id}
                                    onChange={e => setNewTask({ ...newTask, responsavel_id: e.target.value })}
                                    disabled={!fieldsUnlocked}
                                    className="w-full px-4 py-2 border border-gray-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-white disabled:cursor-not-allowed"
                                >
                                    <option value="" className="dark:bg-slate-900">Selecione...</option>
                                    {users.filter(u => {
                                        if (u.status === 'inactive') return false;
                                        if (selectedDepartamento && !temDepartamento(u, selectedDepartamento)) return false;

                                        const userRole = profile?.role;
                                        const userId = user?.id;

                                        if (userRole === 'adm' || userRole === 'gerente') return true;
                                        if (userRole === 'gerente_parcerias') return u.id === userId || u.role === 'parceiro' || u.role === 'vendedor_parceiro';
                                        if (userRole === 'parceiro') return u.id === userId || (u.role === 'vendedor_parceiro' && u.parceiro_id === userId);
                                        if (userRole === 'vendedor_parceiro' || userRole === 'vendedor') return u.id === userId;
                                        return true;
                                    }).map(u => (
                                        <option key={u.id} value={u.id} className="dark:bg-slate-900">{u.full_name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className="space-y-1">
                            <label htmlFor="task-prioridade" className="text-sm font-medium text-gray-700 dark:text-slate-300">Prioridade <span className="text-red-500">*</span></label>
                            <select
                                id="task-prioridade"
                                aria-describedby={pedeCriterio ? 'task-criterios-ajuda' : undefined}
                                value={newTask.prioridade}
                                onChange={e => setNewTask({ ...newTask, prioridade: e.target.value as TarefaPrioridade })}
                                disabled={!fieldsUnlocked}
                                className="w-full px-4 py-2 border border-gray-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-white disabled:cursor-not-allowed"
                            >
                                <option value="baixa" className="dark:bg-slate-900">Baixa</option>
                                <option value="media" className="dark:bg-slate-900">Média</option>
                                <option value="alta" className="dark:bg-slate-900">Alta</option>
                                {newTask.categoria === 'solicitacao' && (
                                    <option value="urgencia" disabled={urgenciaCount >= 3} className="dark:bg-slate-900">
                                        Urgência{urgenciaCount >= 3 ? ' (limite de 3 atingido)' : ''}
                                    </option>
                                )}
                            </select>
                            {pedeCriterio && (() => {
                                const ehUrgencia = newTask.prioridade === 'urgencia';
                                const okAlta = CRITERIOS_ALTA.some(c => criterios.includes(c.id));
                                const okExtra = CRITERIOS_URGENCIA_EXTRA.some(c => criterios.includes(c.id));
                                const opcao = (c: { id: string; texto: string }) => {
                                    const marcado = criterios.includes(c.id);
                                    return (
                                        <button
                                            key={c.id}
                                            type="button"
                                            role="checkbox"
                                            aria-checked={marcado}
                                            onClick={() => alternarCriterio(c.id)}
                                            className={`flex items-center gap-2.5 w-full text-left rounded-lg border px-3 py-2.5 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${marcado
                                                ? 'border-primary bg-primary/5 text-gray-900 dark:text-white dark:bg-primary/15'
                                                : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-600 dark:text-slate-300 hover:border-gray-300 dark:hover:border-slate-600'}`}
                                        >
                                            {marcado
                                                ? <CheckCircle2 size={18} className="shrink-0 text-primary" />
                                                : <Circle size={18} className="shrink-0 text-gray-300 dark:text-slate-600" />}
                                            <span className="leading-snug">{c.texto}</span>
                                        </button>
                                    );
                                };
                                const etapa = (n: number, titulo: string, ok: boolean) => (
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold ${ok ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400'}`}>
                                            {ok ? <Check size={12} /> : n}
                                        </span>
                                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">{titulo}</span>
                                    </div>
                                );
                                return (
                                    <div className="mt-3 rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-800/40 p-4 space-y-4">
                                        <div className="flex items-start gap-2.5">
                                            <div className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${ehUrgencia ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' : 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'}`}>
                                                <AlertTriangle size={15} />
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">
                                                    Por que é {ehUrgencia ? 'Urgência' : 'Alta'}?
                                                </p>
                                                <p id="task-criterios-ajuda" className="text-xs text-gray-500 dark:text-slate-400">
                                                    O motivo vai junto na descrição. Se nada disso se aplica, use Média.
                                                </p>
                                            </div>
                                        </div>
                                        <div>
                                            {etapa(1, 'Qual o impacto', okAlta)}
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">{CRITERIOS_ALTA.map(opcao)}</div>
                                        </div>
                                        {ehUrgencia && (
                                            <div>
                                                {etapa(2, 'O que torna urgente', okExtra)}
                                                <div className="grid grid-cols-1 gap-2">{CRITERIOS_URGENCIA_EXTRA.map(opcao)}</div>
                                            </div>
                                        )}
                                        {/* O botão desativado não ganha foco nem tem hover no celular: o motivo precisa estar visível. */}
                                        {faltaCriterio && (
                                            <p role="status" className="text-xs text-gray-500 dark:text-slate-400">
                                                {ehUrgencia ? 'Marque uma opção em cada etapa para criar a solicitação.' : 'Marque uma opção para criar a solicitação.'}
                                            </p>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>

                        {/* Vencimento não é escolhido na criação de Solicitação — quem distribui a demanda
                            (Direcionar) define o prazo depois. Tarefa comum continua exigindo vencimento aqui. */}
                        {newTask.categoria === 'tarefa' && (
                            <div className="space-y-1">
                                <label className="text-sm font-medium text-gray-700 dark:text-slate-300">
                                    Vencimento <span className="text-red-500">*</span>
                                </label>
                                <input
                                    required
                                    type="date"
                                    value={newTask.vencimento}
                                    onChange={e => setNewTask({ ...newTask, vencimento: e.target.value })}
                                    disabled={!fieldsUnlocked}
                                    className="w-full px-4 py-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg text-sm dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none color-scheme-dark disabled:cursor-not-allowed"
                                />
                            </div>
                        )}
                    </div>
                    {newTask.categoria === 'solicitacao' && !pedidoParaEquipe && (() => {
                        // #6349: o banco grava o prazo pela prioridade ao salvar; aqui só se mostra.
                        const prazo = prazoDaSolicitacao(newTask.prioridade, new Date());
                        return (
                            <p className="text-xs text-gray-500 dark:text-slate-400 -mt-2">
                                {prazo
                                    ? <>Prazo: <strong className="text-gray-700 dark:text-slate-200">até {new Date(`${prazo}T12:00:00`).toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' })}</strong>, pela prioridade (contando só dias úteis). Quem distribuir pode mudar.</>
                                    : 'Prioridade baixa não tem prazo. Quem distribuir pode definir um.'}
                            </p>
                        );
                    })()}

                    {newTask.categoria === 'tarefa' && (
                    <div className="border-t border-gray-100 dark:border-slate-800/50 pt-4 mt-2">
                        <div className="flex items-center gap-2 mb-2">
                            <CheckSquare size={16} className="text-gray-500 dark:text-slate-400" />
                            <span className="text-sm font-medium text-gray-700 dark:text-slate-300">Checklist</span>
                        </div>
                        <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
                            {newTask.checklists.map(item => (
                                <div key={item.id} className="flex items-center gap-2 group">
                                    <div className="flex-1 px-3 py-1.5 bg-gray-50 dark:bg-slate-800/50 border border-gray-100 dark:border-slate-800/50 rounded-lg text-sm flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={item.completed}
                                            onChange={(e) => {
                                                const updated = newTask.checklists.map(c =>
                                                    c.id === item.id ? { ...c, completed: e.target.checked } : c
                                                );
                                                setNewTask({ ...newTask, checklists: updated });
                                            }}
                                            className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-primary focus:ring-primary outline-none bg-transparent"
                                        />
                                        {editingChecklistId === item.id ? (
                                            <input
                                                type="text"
                                                value={editChecklistValue}
                                                onChange={(e) => setEditChecklistValue(e.target.value)}
                                                onBlur={() => {
                                                    if (editChecklistValue.trim() !== item.text) {
                                                        const updated = newTask.checklists.map(c =>
                                                            c.id === item.id ? { ...c, text: editChecklistValue.trim() || item.text } : c
                                                        );
                                                        setNewTask({ ...newTask, checklists: updated });
                                                    }
                                                    setEditingChecklistId(null);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                                                }}
                                                autoFocus
                                                className="flex-1 bg-transparent border-none outline-none text-gray-700 dark:text-slate-200 focus:ring-0 px-1 -mx-1"
                                                placeholder="Item do checklist..."
                                            />
                                        ) : (
                                            <span
                                                className="flex-1 text-gray-700 dark:text-slate-200 cursor-text hover:bg-gray-200/50 dark:hover:bg-slate-700/50 rounded px-1 -mx-1"
                                                onClick={() => { setEditChecklistValue(item.text); setEditingChecklistId(item.id); }}
                                            >
                                                {item.text}
                                            </span>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setNewTask({ ...newTask, checklists: newTask.checklists.filter(c => c.id !== item.id) })}
                                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <input
                                type="text"
                                value={newChecklistText}
                                onChange={(e) => setNewChecklistText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        if (newChecklistText.trim()) {
                                            setNewTask({ ...newTask, checklists: [...newTask.checklists, { id: Math.random().toString(36).substring(2, 9) + Date.now().toString(), text: newChecklistText.trim(), completed: false }] });
                                            setNewChecklistText('');
                                        }
                                    }
                                }}
                                className="flex-1 px-3 py-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg text-sm dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                                placeholder="Adicionar item (Pressione Enter)"
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    if (newChecklistText.trim()) {
                                        setNewTask({ ...newTask, checklists: [...newTask.checklists, { id: Math.random().toString(36).substring(2, 9) + Date.now().toString(), text: newChecklistText.trim(), completed: false }] });
                                        setNewChecklistText('');
                                    }
                                }}
                                disabled={!newChecklistText.trim()}
                                className="px-4 py-2 bg-primary/10 text-primary hover:bg-primary/20 dark:bg-primary/20 rounded-lg text-sm font-semibold disabled:opacity-50 transition-colors whitespace-nowrap"
                            >
                                Adicionar
                            </button>
                        </div>
                    </div>
                    )}

                    <div className="border-t border-gray-100 dark:border-slate-800/50 pt-4 mt-2">
                        <div className="flex items-center gap-2 mb-2">
                            <Upload size={16} className="text-gray-500 dark:text-slate-400" />
                            <span className="text-sm font-medium text-gray-700 dark:text-slate-300">Anexos</span>
                        </div>
                        {/* Label customizado em vez do <input type="file"> nu — o texto nativo do navegador
                            ("Nenhum arquivo selecionado"/"Nenhum ficheiro selecionado", dependendo do idioma
                            do navegador) nunca reflete o que foi de fato anexado, porque resetamos
                            e.target.value logo depois de ler o arquivo (de propósito, pra permitir selecionar
                            de novo / complementar a lista). Isso deixava a única fonte de verdade real — a
                            lista de chips abaixo — escondida atrás de um texto nativo enganoso. */}
                        <label className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold bg-primary/10 dark:bg-primary/20 text-primary hover:bg-primary/20 cursor-pointer transition-colors w-fit">
                            <Plus size={12} /> Escolher arquivos
                            <input
                                type="file"
                                multiple
                                onChange={e => {
                                    const picked = Array.from(e.target.files || []);
                                    setFiles(prev => [...prev, ...picked]);
                                    // Reseta pra permitir escolher o mesmo arquivo de novo / complementar a lista.
                                    e.target.value = '';
                                }}
                                className="hidden"
                            />
                        </label>
                        {files.length === 0 ? (
                            <p className="text-xs text-gray-400 dark:text-slate-500 italic mt-2">Nenhum arquivo selecionado ainda.</p>
                        ) : (
                            <div className="mt-2 space-y-1.5">
                                {files.map((file, idx) => (
                                    <div key={idx} className="flex items-center justify-between gap-2 px-3 py-1.5 bg-gray-50 dark:bg-slate-800/50 border border-gray-100 dark:border-slate-800/50 rounded-lg text-xs">
                                        <span className="truncate text-gray-600 dark:text-slate-300">{file.name}</span>
                                        <button
                                            type="button"
                                            onClick={() => setFiles(prev => prev.filter((_, i) => i !== idx))}
                                            className="text-gray-400 hover:text-red-500 shrink-0"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col sm:flex-row justify-end pt-4 border-t border-gray-100 dark:border-slate-800/50 gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-full sm:w-auto px-6 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors text-sm"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={creating || faltaCriterio}
                            title={faltaCriterio ? 'Marque o critério da prioridade escolhida' : undefined}
                            className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-primary text-white font-semibold hover:bg-primary/90 transition-all shadow-lg shadow-primary/30 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed text-sm"
                        >
                            {creating ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                            {creating ? 'Salvando...' : (newTask.categoria === 'solicitacao' ? 'Criar Solicitação' : 'Criar Tarefa')}
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
};

export default TaskModal;